import React, { useCallback, useMemo } from "react";
import Tooltip from "@mui/material/Tooltip";
import { getUIColorPalette } from "../../theme/ColorPalette";

interface MiniModalSwitchProps {

    /** Label shown when the switch is enabled. */
    "activeText": string;

    /** Label shown when the switch is disabled. */
    "inactiveText": string;

    /** Current switch value. */
    "value": boolean;

    /** Called when the switch value changes. */
    "onChange": ( value: boolean ) => void;

    /** Enables dark palette variants for text and control colors. */
    "isDarkUi"?: boolean;

    /** Disables switch interactions when true. */
    "disabled"?: boolean;

    /** Tooltip displayed on hover when the switch is disabled. */
    "disabledTooltip"?: string;
}

/**
 * Reusable on/off switch row for mini-modal settings.
 * Displays text on the left and the switch control on the right.
 * @param props Switch labels, value, and change callback.
 * @returns Styled switch row component.
 */
const MiniModalSwitch: React.FC<MiniModalSwitchProps> = ( {
    activeText,
    inactiveText,
    value,
    onChange,
    isDarkUi = false,
    disabled = false,
    disabledTooltip,
} ) => {
    const palette = useMemo( () => getUIColorPalette( isDarkUi ), [ isDarkUi ] );

    const handleChange = useCallback( ( event: React.ChangeEvent<HTMLInputElement> ) => {
        onChange( event.target.checked );
    }, [ onChange ] );

    return (
        <div className="flex w-full items-center justify-between gap-3">
            <span
                className="text-sm"
                style={isDarkUi ? { "color": palette.overlayTextPrimary } : undefined}
            >
                {value ? activeText : inactiveText}
            </span>
            <Tooltip
                arrow
                disableFocusListener={disabled === false}
                disableHoverListener={disabled === false}
                disableTouchListener={disabled === false}
                placement="top"
                slotProps={{ "popper": { "sx": { "zIndex": 2147483647 } } }}
                title={disabled === true && disabledTooltip !== undefined ? disabledTooltip : undefined}
            >
                <label
                    className={`relative inline-flex items-center ${ disabled === true ? "cursor-not-allowed" : "cursor-pointer" }`}
                >
                    <input
                        checked={value}
                        className="peer sr-only"
                        disabled={disabled}
                        onChange={handleChange}
                        type="checkbox"
                    />
                    <span
                        aria-hidden="true"
                        className="h-7 w-12 rounded-full border border-slate-300 bg-slate-300 transition-colors duration-200 peer-checked:bg-emerald-500"
                        style={{
                            "backgroundColor": isDarkUi && value === false ? palette.overlayCardBackgroundSoft : undefined,
                            "border":          isDarkUi ? `1px solid ${ palette.overlayBorder }` : undefined,
                            "opacity":         disabled === true ? 0.7 : 1,
                        }}
                    />
                    <span
                        aria-hidden="true"
                        className="absolute left-1 top-1 h-5 w-5 rounded-full bg-white shadow transition-transform duration-200 peer-checked:translate-x-5"
                        style={{
                            ...isDarkUi ? { "backgroundColor": palette.overlayTextPrimary } : {},
                            "opacity": disabled === true ? 0.7 : 1,
                        }}
                    />
                </label>
            </Tooltip>
        </div>
    );
};

export default MiniModalSwitch;
